import { Todo, TodoColor } from "./types";
import { supabase } from "@/integrations/supabase/client";

const LEGACY_KEY = "todo_tarefas";
const MIGRATION_FLAG = "todo_tarefas_migrado";
export const TODOS_CHANGED_EVENT = "todos:changed";

type Row = {
  id: string;
  title: string;
  description: string | null;
  date: string | null;
  color: string;
  completed: boolean;
  created_at: string;
};

function rowToTodo(r: Row): Todo {
  return {
    id: r.id,
    title: r.title,
    description: r.description ?? undefined,
    date: r.date ?? undefined,
    color: (r.color as TodoColor) || "blue",
    completed: !!r.completed,
    createdAt: new Date(r.created_at).getTime(),
  };
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

function notify() {
  try {
    window.dispatchEvent(new CustomEvent(TODOS_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

export async function loadTodos(): Promise<Todo[]> {
  const uid = await currentUserId();
  if (!uid) return [];
  const { data, error } = await supabase
    .from("todos")
    .select("id,title,description,date,color,completed,created_at")
    .eq("user_id", uid)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as Row[] | null)?.map(rowToTodo) ?? [];
}

export async function saveTodo(todo: Todo): Promise<Todo> {
  const uid = await currentUserId();
  if (!uid) throw new Error("Usuário não autenticado");
  const payload = {
    id: todo.id,
    user_id: uid,
    title: todo.title,
    description: todo.description ?? null,
    date: todo.date ?? null,
    color: todo.color,
    completed: todo.completed,
  };
  const { data, error } = await supabase
    .from("todos")
    .upsert(payload, { onConflict: "id" })
    .select("id,title,description,date,color,completed,created_at")
    .single();
  if (error) throw error;
  notify();
  return rowToTodo(data as Row);
}

export async function deleteTodo(id: string): Promise<void> {
  const { error } = await supabase.from("todos").delete().eq("id", id);
  if (error) throw error;
  notify();
}

function readLegacyLocal(): Todo[] {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((t) => t && t.id && t.title) as Todo[];
  } catch {
    return [];
  }
}

export async function migrateLocalTodosIfNeeded(): Promise<number> {
  if (localStorage.getItem(MIGRATION_FLAG) === "true") return 0;
  const legacy = readLegacyLocal();
  if (legacy.length === 0) {
    localStorage.setItem(MIGRATION_FLAG, "true");
    return 0;
  }
  const uid = await currentUserId();
  if (!uid) return 0;
  const rows = legacy.map((t) => ({
    id: t.id,
    user_id: uid,
    title: t.title,
    description: t.description ?? null,
    date: t.date ?? null,
    color: t.color || "blue",
    completed: !!t.completed,
  }));
  const { error } = await supabase.from("todos").upsert(rows, { onConflict: "id" });
  if (error) throw error;
  localStorage.setItem(MIGRATION_FLAG, "true");
  return rows.length;
}